import { AppConfig } from 'AppConfig';
import { jwtDecode } from 'jwt-decode'; // Updated import statement
import * as Types from './userTypes';

const initialState = {
  allBrokers: [],
  apps: [],
  companies: [],
  currentApp: null,
  currentBroker: '',
  currentTenantCd: null,
  defaultUserState: true,
  email: '',
  enigmaAdmin: false,
  firstName: '',
  foAdmin: false,
  hasEnigmaAccess: false,
  enigmaAccessTenantCds: [],
  headerType: AppConfig.HEADER_MENU_TYPE.STANDARD,
  id: -1,
  external: false,
  isUpdating: false,
  lastLoginDt: undefined,
  lastName: '',
  mdAdmin: false,
  permissions: [],
  planOptimizeAdmin: false,
  securityAdmin: false,
  sysAdmin: false,
  tableauRole: '',
  tableauServiceUrl: null,
  tableauUsername: '',
  tenantApps: [],
  tenantFeatures: {},
  termsAccepted: false,
  aiSummaryAvailable: null,
  aiSummaryEnabled: null,
  username: '',
  enigmaTenantAdmin: false,
  planOptimizeTenantAdmin: false
};

const sortCompanies = (companies) => {
  return companies && Array.isArray(companies)
    ? companies.sort((first, second) =>
        first.name.toLowerCase().localeCompare(second.name.toLowerCase())
      )
    : [];
};

const sortBrokers = (brokers) => {
  return brokers && Array.isArray(brokers)
    ? brokers.sort((first, second) =>
        first.name.toLowerCase().localeCompare(second.name.toLowerCase())
      )
    : [];
};

const resolveAiSummaryEnabled = (rawValue, fallbackValue) => {
  if (rawValue === undefined || rawValue === null) {
    return fallbackValue;
  }

  if (rawValue === false || rawValue === 'false' || rawValue === 0 || rawValue === '0') {
    return false;
  }

  if (rawValue === true || rawValue === 'true' || rawValue === 1 || rawValue === '1') {
    return true;
  }

  return fallbackValue;
};

const resolveAiSummaryAvailable = (payload) => {
  const payloadAiSummaryAvailable = resolveAiSummaryEnabled(
    payload?.aiSummaryAvailable,
    null
  );

  if (payloadAiSummaryAvailable !== null) {
    return payloadAiSummaryAvailable;
  }

  const currentTenantCd = payload?.currentTenantCd;
  const currentCompany = Array.isArray(payload?.companies)
    ? payload.companies.find((company) => company?.code === currentTenantCd)
    : null;

  const companyAiSummaryEnabled = resolveAiSummaryEnabled(
    currentCompany?.aiSummaryEnabled,
    null
  );

  if (companyAiSummaryEnabled !== null) {
    return companyAiSummaryEnabled;
  }

  return resolveAiSummaryEnabled(
    payload?.tenantFeatures?.[currentTenantCd]?.aiSummaryEnabled,
    null
  );
};

const userReducer = (state = initialState, action) => {
  switch (action.type) {
    case Types.TENANT_SWITCH_START:
      return {
        ...state,
        isUpdating: true
      };
    case Types.FETCH_DATA_SUCCESS:
      return {
        ...state,
        id: action.payload.userId,
        email: action.payload.email,
        username: action.payload.username,
        currentApp: action.payload.currentApp,
        currentTenantCd: action.payload.currentTenantCd,
        apps: action.payload.apps,
        permissions: action.payload.permissions,
        companies: sortCompanies(action.payload.companies),
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        external: action.payload.external,
        isUpdating: false,
        tenantApps: action.payload.tenantApps,
        hasEnigmaAccess: action.payload.hasEnigmaAccess,
        enigmaAccessTenantCds: Array.isArray(action.payload.enigmaAccessTenantCds)
          ? action.payload.enigmaAccessTenantCds
          : [],
        tableauServiceUrl: action.payload?.tbUrl,
        defaultUserState: false,
        sysAdmin: action.payload.systemAdmin,
        mdAdmin: action.payload.mdAdmin,
        foAdmin: action.payload.foAdmin,
        enigmaAdmin: action.payload?.enigmaAdmin,
        planOptimizeAdmin: action.payload?.planOptimizeAdmin,
        enigmaTenantAdmin: action.payload?.enigmaTenantAdmin,
        planOptimizeTenantAdmin: action.payload?.planOptimizeTenantAdmin,
        aiSummaryEnabled: resolveAiSummaryEnabled(
          action.payload?.aiSummaryEnabled ?? action.payload?.isAiSummaryEnabled ?? action.payload?.ai_summary_enabled,
          state.aiSummaryEnabled
        ),
        aiSummaryAvailable: resolveAiSummaryAvailable(action.payload),
        tenantFeatures: action.payload?.tenantFeatures || {}
      };
    case Types.LOGIN_SUCCESS:
      return {
        ...state,
        apps: action.payload.apps || state.apps,
        companies: sortCompanies(action.payload.companies) || state.companies,
        currentApp: action.payload.currentApp || state.currentApp,
        currentTenantCd:
          action.payload.currentTenantCd || state.currentTenantCd,
        defaultUserState: false,
        email: action.payload.email || state.email,
        enigmaAdmin: action.payload.isEnigmaAdmin || state.enigmaAdmin,
        firstName: action.payload.firstName || state.firstName,
        foAdmin: action.payload.foAdmin || state.foAdmin,
        hasEnigmaAccess:
          action.payload.hasEnigmaAccess || state.hasEnigmaAccess,
        enigmaAccessTenantCds: Array.isArray(action.payload.enigmaAccessTenantCds)
          ? action.payload.enigmaAccessTenantCds
          : state.enigmaAccessTenantCds,
        id: action.payload.id || state.id, //I only see id, not userId in the result.
        isUpdating: false,
        lastLoginDt: action.payload.lastLoginDt || state.lastLoginDt,
        lastName: action.payload.lastName || state.lastName,
        mdAdmin: action.payload.mdAdmin || state.mdAdmin,
        permissions: action.payload.permissions || state.permissions,
        planOptimizeAdmin:
          action.payload.isPlanOptimizeAdmin || state.planOptimizeAdmin,
        sysAdmin: action.payload.systemAdmin || state.sysAdmin,
        tableauUsername:
          action.payload.tableauUsername || state.tableauUsername,
        tenantApps: action.payload.tenantApps || state.tenantApps,
        tenantFeatures:
          action.payload.tenantFeatures !== undefined
            ? action.payload.tenantFeatures
            : initialState.tenantFeatures,
        aiSummaryEnabled: resolveAiSummaryEnabled(
          action.payload.aiSummaryEnabled ?? action.payload.isAiSummaryEnabled ?? action.payload.ai_summary_enabled,
          state.aiSummaryEnabled
        ),
        aiSummaryAvailable: resolveAiSummaryAvailable(action.payload),
        username: action.payload.username || state.username
      };
    case Types.SET_AI_SUMMARY_PREFERENCE:
      return {
        ...state,
        aiSummaryEnabled: resolveAiSummaryEnabled(action.payload, state.aiSummaryEnabled)
      };
    case Types.UPDATE_COMPANY_FEATURES: {
      const updatedCompanies = state.companies.map((company) => {
        const isUpdatedCompany = action.payload?.id != null
          ? String(company?.id) === String(action.payload.id)
          : company?.code === action.payload?.code;
        return isUpdatedCompany ? { ...company, ...action.payload } : company;
      });
      // Re-derive aiSummaryAvailable for the active tenant so Quick View visibility
      // updates immediately when AI Summary is toggled, without a page refresh.
      const updatedCurrentCompany = updatedCompanies.find(
        (company) => company?.code === state.currentTenantCd
      );
      return {
        ...state,
        companies: updatedCompanies,
        aiSummaryAvailable: updatedCurrentCompany
          ? resolveAiSummaryEnabled(updatedCurrentCompany.aiSummaryEnabled, state.aiSummaryAvailable)
          : state.aiSummaryAvailable
      };
    }
    case Types.LOGOUT:
    case Types.FETCH_DATA_FAILURE:
    case Types.LOGIN_FAILURE:
      return initialState;
    case Types.BROKER_SET:
      return {
        ...state,
        currentBroker: action.payload
      };
    case Types.APP_SWITCHED:
      return {
        ...state,
        headerType: action.payload
      };
    case Types.JWT:
      try {
        const jwtObj = jwtDecode(action.payload);
        return {
          ...state,
          // Redux is rebuilt on a browser refresh. Preserve the tenant encoded
          // in the session token so the bootstrap request is scoped correctly.
          currentTenantCd: jwtObj.tenantCd || state.currentTenantCd,
          permissions: jwtObj.permission,
          apps: jwtObj.apps,
          firstName: jwtObj.firstName,
          lastName: jwtObj.lastName,
          tenantApps: jwtObj.KEY_JWT_TENANT_APPS,
          jwtExp: jwtObj.exp || state.jwtExp
        };
      } catch (error) {
        //donothing
        console.error('invalid JWT: ' + action.payload);
        return state;
      }
    case Types.FETCH_ALL_BROKERS:
      return {
        ...state,
        allBrokers: sortBrokers(action.payload)
      };
    case Types.RESET_APPS_TENANT:
      return {
        ...state,
        currentApp: null,
        currentTenantCd: null
      };
      case Types.RESET_APP:
        return {
          ...state,
          currentApp: null,
        };
    default:
      return state;
  }
};

export default userReducer;
